import { z } from 'zod';

import { stripInvisible } from './text.js';

const DESIGN_SPEC_SOURCES = ['stated', 'assumed'] as const;

export const MAX_DESIGN_SPEC_NAME_CHARS = 60;
export const MAX_DESIGN_SPEC_VALUE_CHARS = 120;
const MAX_DESIGN_SPEC_NOTE_CHARS = 400;

/** Entries one patch may carry, per operation. `applyPatch` resolves each entry
 *  against an index it builds once, but the whole merge still runs inside the
 *  transaction holding `SELECT … FOR UPDATE` on the conversation row, so the
 *  request is bounded rather than the table. Generous against a 29-row
 *  worksheet; the route answers 400 above it. */
export const MAX_DESIGN_SPEC_PATCH_ENTRIES = 200;

// Cells are sanitised on the way IN, not on the way out. `indexOfId` matches the
// STORED string while the rendered views are what the agent reads, so a strip
// applied at render would make a row the agent can see and cannot address. It
// also keeps the duplicate-name guard able to see a collision between a pasted
// name and a typed one.
// U+00A0 becomes a normal space rather than being deleted: it is a visible gap
// in a name pasted out of a PDF, and dropping it would join two words.
const sanitizeCell = (s: string): string => stripInvisible(s.replace(/\u00a0/g, ' '));

// Every cell the agent or the user can write is ONE LINE. A `\n` in a cell
// reproduces the line structure of the rendered views, so its text can forge the
// renderer's own headings ('assumed by you', 'stated by the user') inside the
// trusted <design_brief_update> block, or extra rows inside the sheet grid — a
// row that does not exist. Values reach these renderers from uploaded patents
// and PDFs by way of `source: 'assumed'`, so this is not only self-injection.
// The renderers escape nothing, so a cell's characters are emitted verbatim
// into a line-oriented, space-padded grid.
// These five are the line and tab characters `sanitizeCell` leaves behind: it
// preserves \t, \n and \r, and U+2028/U+2029 are outside its ranges. They are
// REJECTED rather than stripped, so a multi-line paste is a 400 the user sees
// rather than a silent join. Every other C0/C1 control is already gone.
// The real spec values this rejects nothing of are single-line by nature:
// '−20 … +60', '60 / 45 / 35', '435.8 – 656.3'.
// `note` is exempt — the patch schema has no `note` field, so no agent or user
// input reaches it.
const SINGLE_LINE = /^[^\r\n\t\u2028\u2029]*$/;
const SINGLE_LINE_MESSAGE = 'must be a single line: line breaks and tabs are not allowed';

/** A bounded one-line cell. */
const cell = (max: number) =>
  z.string().overwrite(sanitizeCell).max(max).regex(SINGLE_LINE, SINGLE_LINE_MESSAGE);

// Identity fields carry `.trim()` BEFORE `.min(1)`: zod applies the checks in
// chain order, so a whitespace-only string is rejected rather than stored, and a
// padded one is normalised. The reverse order lets '  ' through, which would
// then silently fail to match the row it names.
// `.normalize('NFC')` sits in the same chain: without it the two Unicode
// encodings of one visible name ('Å' as U+00C5 vs 'A' + U+030A) are two
// different rows. NFC composes; it does not fold distinct characters, so 'µ'
// (U+00B5) and 'μ' (U+03BC) remain two names.
/** A row identifier — the string `indexOfId` resolves. */
const identity = () =>
  z.string().overwrite(sanitizeCell).trim().normalize('NFC').min(1)
    .regex(SINGLE_LINE, SINGLE_LINE_MESSAGE);

/** One row of the design spec. `key` is the canonical identifier for a seeded
 *  row, the row's own name for a row added INTO a section, and null for a row
 *  that belongs to none — see designSpecSections, where the null is what files
 *  a row under `Added`. `value` is a STRING because real specs are ranges and
 *  lists ("−20 … +60", "435.8 – 656.3", "60 / 45 / 35") — numeric typing would
 *  force a parser v1 has no consumer for. */
export const DesignSpecRowSchema = z
  .object({
    key: identity().nullable(),
    name: identity().max(MAX_DESIGN_SPEC_NAME_CHARS),
    // null is the unset state, so a stored '' would read as set to nothing —
    // the filled count and the renderers all test `value === null`. Written out
    // rather than routed through `cell`, because the cap has to apply to the
    // TRIMMED value: a 121-char value with a trailing space is accepted, and
    // `cell` would reject it before the trim ran.
    value: z.string().overwrite(sanitizeCell).trim().min(1).max(MAX_DESIGN_SPEC_VALUE_CHARS)
      .regex(SINGLE_LINE, SINGLE_LINE_MESSAGE).nullable(),
    source: z.enum(DESIGN_SPEC_SOURCES).nullable(),
    removed: z.boolean(),
    /** Not patchable — the patch schema has no `note` field. */
    note: z.string().max(MAX_DESIGN_SPEC_NOTE_CHARS),
  })
  .refine((r) => (r.value === null) === (r.source === null), {
    message: 'value and source are set together',
  });
export type DesignSpecRow = z.infer<typeof DesignSpecRowSchema>;

export const DesignSpecSchema = z.array(DesignSpecRowSchema);

/** The id a row is addressed by — what `indexOfId` resolves and the rendered
 *  views print. */
export const designSpecIdentifier = (r: DesignSpecRow): string => r.key ?? r.name;

/** An entry the merge could not apply. A 200 carrying one of these means that
 *  part of the edit did NOT land. */
export interface DesignSpecSkip {
  op: 'set' | 'add' | 'remove';
  /** The entry's position in `patch[op]`: two entries of one op can share an
   *  id, and only the position tells the applied one from the skipped one. */
  index: number;
  id: string;
  reason: 'no_match' | 'removed' | 'duplicate';
}

/** Section heads for the seeded worksheet, each anchored to the `key` of the
 *  row its section starts at. Anchors rather than positions because inserting a
 *  seed row must not silently relabel every row below it — the anchors are
 *  asserted against `seedRows()` in tests/services/designSpec/seed.test.ts. */
export const DESIGN_SPEC_SECTIONS = [
  { startKey: 'configuration', label: 'Optical' },
  { startKey: 'sensor_active', label: 'Sensor and image' },
  { startKey: 'overall_length', label: 'Mechanical' },
  { startKey: 'transmission_min', label: 'Performance' },
  { startKey: 'operating_temp', label: 'Environment' },
] as const satisfies ReadonlyArray<{ startKey: string; label: string }>;

export type DesignSpecSection = typeof DESIGN_SPEC_SECTIONS[number]['label'];

/** The labels a patch may name, derived from the anchors so the two cannot
 *  drift in either direction — a label with no anchor row would resolve to no
 *  position at all. */
export const DESIGN_SPEC_SECTION_LABELS = DESIGN_SPEC_SECTIONS.map((s) => s.label);

/** Rows belonging to no section — what an `add` that names none produces. */
export const DESIGN_SPEC_ADDED_SECTION = 'Added';

/**
 * The section each stored row falls under, in stored order. The single
 * encoding of the rule: the panel groups its heads on it and the merge inserts
 * against it.
 *
 * A section runs from its anchor key to the next anchor, tombstones included —
 * a removed anchor still holds its section's place. A NULL key is what opts a
 * row out and files it under `Added`, which is why a row added INTO a section
 * takes its own name as its key: any key that is not an anchor simply inherits.
 */
export function designSpecSections(
  rows: readonly DesignSpecRow[],
): Array<DesignSpecSection | typeof DESIGN_SPEC_ADDED_SECTION> {
  const byStartKey = new Map<string, DesignSpecSection>(
    DESIGN_SPEC_SECTIONS.map((s) => [s.startKey, s.label]),
  );
  let section: DesignSpecSection | typeof DESIGN_SPEC_ADDED_SECTION = DESIGN_SPEC_SECTIONS[0].label;
  return rows.map((row) => {
    if (row.key === null) section = DESIGN_SPEC_ADDED_SECTION;
    else section = byStartKey.get(row.key) ?? section;
    return section;
  });
}

export const DesignSpecPatchSchema = z
  .object({
    set: z
      .array(
        z.object({
          // An id that matches no row is echoed back into the receipt verbatim
          // (updateDesignSpec's `label`), so it reaches agent-facing text too.
          id: identity(),
          value: cell(MAX_DESIGN_SPEC_VALUE_CHARS),
          source: z.enum(DESIGN_SPEC_SOURCES),
        }),
      )
      .max(MAX_DESIGN_SPEC_PATCH_ENTRIES)
      .optional(),
    add: z
      .array(
        z
          .object({
            name: identity().max(MAX_DESIGN_SPEC_NAME_CHARS),
            // A row may be added unfilled, for the user to answer. Source is
            // nullable rather than optional: an omitted key reads as an
            // oversight, an explicit null as a state.
            value: cell(MAX_DESIGN_SPEC_VALUE_CHARS).optional(),
            source: z.enum(DESIGN_SPEC_SOURCES).nullable(),
            // Where the row goes. Omitted, it lands at the foot of the sheet
            // under Added; named, it lands at the end of that section.
            section: z.enum(DESIGN_SPEC_SECTION_LABELS).optional(),
          })
          .refine((a) => (a.value?.trim() ?? '') === '' || a.source !== null, {
            message: 'an add carrying a value must state its source',
          }),
      )
      .max(MAX_DESIGN_SPEC_PATCH_ENTRIES)
      .optional(),
    remove: z.array(identity()).max(MAX_DESIGN_SPEC_PATCH_ENTRIES).optional(),
  })
  .refine(
    (p) => (p.set?.length ?? 0) + (p.add?.length ?? 0) + (p.remove?.length ?? 0) > 0,
    { message: 'patch must contain at least one of set, add, remove' },
  );
export type DesignSpecPatch = z.infer<typeof DesignSpecPatchSchema>;
